import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { faAngleRight, faPlay } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import CustomEditorHosted from "../../components/common/CustomEditorHosted";
import { useNavigate, useLocation } from "react-router-dom";
import ImageUpload from "../../components/common/ImageUpload";
import toast from "react-hot-toast";
import { addNews, getDetails, editNews } from "../../services/news.js";
import { Form } from "react-bootstrap";
import Select from "react-select";
import {
  getAllDropdowns,
  getMasterData,
  searchIndianCollege,
  getClgList,
} from "../../services/college";
import { getCareerList } from "../../services/career";

const customSelectStyles = {
  control: (base, state) => ({
    ...base,
    backgroundColor: "#1b2438",
    borderColor: state.isFocused ? "#7367f0" : "#283046",
    color: "#fff",
    minHeight: "45px",
    height: "45px",
    borderRadius: "6px",
    boxShadow: "none",
    "&:hover": {
      borderColor: "#7367f0",
    },
  }),
  valueContainer: (base) => ({
    ...base,
    height: "45px",
    padding: "0 12px",
    display: "flex",
    alignItems: "center",
  }),
  input: (base) => ({
    ...base,
    margin: "0px",
    padding: "0px",
    color: "#fff",
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: "45px",
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: "#1b2438",
    zIndex: 99999,
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused
      ? "#7367f0"
      : state.isSelected
      ? "#7367f0"
      : "#1b2438",
    color: state.isFocused || state.isSelected ? "#ffffff" : "#d0d4dc",
    cursor: "pointer",
    "&:active": {
      backgroundColor: "#7367f0",
    },
  }),
  singleValue: (base) => ({
    ...base,
    color: "#fff",
  }),
  placeholder: (base) => ({
    ...base,
    color: "#67748e",
  }),
};

// Define validation schema using Yup
const validationSchema = yup.object().shape({
  category: yup.string().required("Category is required"),
  title: yup
    .string()
    .min(4, "Title must be at least 4 characters")
    .max(200, "Title cannot exceed 200 characters")
    .required("Title is required"),
  cover_image: yup.string().required("Cover image is required"),
  description: yup.string().required("Description is required"),
  time_taken: yup.string().required("Time taken is required"),
  is_popular: yup.string().required("Please select if this news is popular"),
  is_featured: yup.string().required("Please select if this news is featured"),
});

const AddNews = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Check if the URL contains 'edit' and extract the ID properly
  const isEditMode = location.pathname.includes("edit-news");
  const id = isEditMode ? location.pathname.split("/").pop() : null;

  const [tags, setTags] = useState([]);
  const [mainTag, setMainTag] = useState("courses");
  const [selectedSubTag, setSelectedSubTag] = useState("");
  const [subTagOptions, setSubTagOptions] = useState([]);
  const [isSubTagLoading, setIsSubTagLoading] = useState(false);
  const [newsCategories, setNewsCategories] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [editorContent, setEditorContent] = useState("");

  const admin_type = localStorage.getItem("admin_type") || ""; // Get admin type

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      category: "",
      title: "",
      cover_image: "",
      tags: [],
      description: "",
      time_taken: "",
      is_popular: "0",
      is_featured: "0",
    },
    resolver: yupResolver(validationSchema),
  });

  // Fetch news categories dropdown
  useEffect(() => {
    const fetchNewsCategories = async () => {
      try {
        const res = await getAllDropdowns("news_categories");
        const categories =
          res?.data?.result?.news_categories ||
          res?.data?.result ||
          res?.data?.data ||
          [];
        if (Array.isArray(categories) && categories.length > 0) {
          setNewsCategories(categories);
        } else {
          // Fallback to default categories if API response empty
          setNewsCategories([
            { id: 1, name: "Exam Alerts" },
            { id: 2, name: "College News" },
            { id: 3, name: "Scholarships" },
            { id: 4, name: "Admissions" },
            { id: 5, name: "Results" },
            { id: 6, name: "Study Abroad" },
          ]);
        }
      } catch (err) {
        console.error("Error fetching news categories:", err);
        setNewsCategories([
          { id: 1, name: "Exam Alerts" },
          { id: 2, name: "College News" },
          { id: 3, name: "Scholarships" },
          { id: 4, name: "Admissions" },
          { id: 5, name: "Results" },
          { id: 6, name: "Study Abroad" },
        ]);
      }
    };
    fetchNewsCategories();
  }, []);

  const getMainTagApiType = (mTag) => {
    switch (mTag) {
      case "courses":
        return "courses";
      case "careers":
        return "careers";
      case "country":
        return "origin_country";
      case "entrance_exam":
        return "entrance_exam";
      case "specialization":
        return "specialization";
      case "colleges":
        return "colleges";
      case "all":
      default:
        return "courses";
    }
  };

  const fetchSubTagOptions = async (searchQuery = "") => {
    setIsSubTagLoading(true);
    try {
      let items = [];
      if (mainTag === "colleges") {
        try {
          const res = await searchIndianCollege({
            search_key: searchQuery,
            limit: 15,
            page: 1,
          });
          items =
            res?.data?.result || res?.data?.data || res?.data?.colleges || [];
        } catch (e) {
          const res = await getClgList();
          items = res?.data?.result || res?.data?.data || [];
        }
      } else if (mainTag === "careers") {
        try {
          const res = await getCareerList({
            page: 1,
            limit: 50,
            search_key: searchQuery,
          });
          items = res?.data?.result || res?.data?.data || [];
        } catch (e) {
          console.error("Error fetching careers:", e);
          items = [];
        }
      } else {
        const apiType = getMainTagApiType(mainTag);
        try {
          const res = await getMasterData(apiType, searchQuery, 15);
          items = res?.data?.result || res?.data?.data || [];
        } catch (e) {
          const res = await getAllDropdowns(apiType);
          const dataObj = res?.data?.result || res?.data?.data || {};
          items = dataObj[apiType] || dataObj[mainTag] || [];
        }
      }

      let mapped = (items || [])
        .map((item) => {
          const val =
            typeof item === "string"
              ? item
              : item?.career_name ||
                item?.clg_name ||
                item?.college_name ||
                item?.name ||
                item?.title ||
                item?.label ||
                item?.id ||
                "";
          return { value: val, label: val };
        })
        .filter((opt) => opt.value);

      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        mapped = mapped.filter((opt) => opt.label.toLowerCase().includes(q));
      }

      setSubTagOptions(mapped.slice(0, 50));
    } catch (error) {
      console.error("Error fetching subtag options:", error);
      setSubTagOptions([]);
    } finally {
      setIsSubTagLoading(false);
    }
  };

  useEffect(() => {
    fetchSubTagOptions("");
  }, [mainTag]);

  const handleRemoveTag = (indexToRemove) => {
    const newTags = tags.filter((_, index) => index !== indexToRemove);
    setTags(newTags);
  };

  // Fetch news details for edit mode
  useEffect(() => {
    const fetchNewsDetails = async () => {
      if (isEditMode && id && id !== "edit-news") {
        setIsLoading(true);

        try {
          const response = await getDetails(id);
          if (
            response &&
            response.data.statusCode === 200 &&
            response.data.result
          ) {
            const newsData = response.data.result;

            // Resolve category: supports numeric ID, string ID, or category name
            const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
            let catValue = String(newsData.category || "");
            if (isNaN(catValue) && newsData.category) {
              const matched = newsCategories.find(
                (c) =>
                  norm(c.name || c.category_name || c.title) === norm(newsData.category)
              );
              if (matched && matched.id !== undefined) {
                catValue = String(matched.id);
              }
            }
            setValue("category", catValue);
            setValue("title", newsData.title || "");
            setValue("cover_image", newsData.cover_image || "");
            setValue("description", newsData.description || "");
            setValue("time_taken", newsData.time_taken || "");
            setValue("is_popular", String(newsData.is_popular ?? "0"));
            setValue("is_featured", String(newsData.is_featured ?? "0"));

            // Handle tags (can be array, JSON string, or comma-separated string)
            if (newsData.tags) {
              if (Array.isArray(newsData.tags)) {
                setTags(newsData.tags);
              } else if (typeof newsData.tags === "string") {
                try {
                  const parsed = JSON.parse(newsData.tags);
                  if (Array.isArray(parsed)) {
                    setTags(parsed);
                  } else {
                    setTags(
                      newsData.tags
                        .split(",")
                        .map((t) => t.trim())
                        .filter(Boolean)
                    );
                  }
                } catch (e) {
                  setTags(
                    newsData.tags
                      .split(",")
                      .map((t) => t.trim())
                      .filter(Boolean)
                  );
                }
              }
            }

            // Set editor content
            setEditorContent(newsData.description || "");

            console.log("News details loaded successfully for editing");
          } else {
            toast.error("Failed to load news details");
          }
        } catch (error) {
          console.error("Error fetching news details:", error);
          toast.error(`Failed to load news details: ${error.message}`);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchNewsDetails();
  }, [id, isEditMode, setValue, newsCategories]);

  const handleImageUpload = (logo, url) => {
    console.log(url, "Cover Image");
    setValue("cover_image", url);
    setIsUploadingImage(false);
    toast.success("Image uploaded successfully");
  };

  const handleImageUploadStart = () => {
    setIsUploadingImage(true);
    console.log("Upload started...");
  };

  // Submit form
  const onSubmit = async (data) => {
    if (isUploadingImage) {
      toast.error("Please wait for image upload to complete");
      return;
    }

    setIsLoading(true);
    try {
      if (typeof data.description === "object" && data.description !== null) {
        data.description = data.description.toString();
      }

      // Backend expects category Master ID (e.g. 1, 2, 3...)
      const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
      let payloadCategory = data.category;
      if (
        !isNaN(payloadCategory) &&
        payloadCategory !== "" &&
        payloadCategory !== null
      ) {
        payloadCategory = Number(payloadCategory);
      } else if (typeof payloadCategory === "string") {
        const matched = newsCategories.find(
          (c) =>
            norm(c.name || c.category_name || c.title) === norm(payloadCategory)
        );
        if (matched && matched.id !== undefined) {
          payloadCategory = Number(matched.id);
        }
      }

      const payload = {
        ...data,
        category: payloadCategory,
        tags: tags, // Array of strings e.g. ["JEE Main", "Exam Alert", "NTA"]
      };

      console.log("Submitting News Data: ", payload);

      if (isEditMode) {
        payload.news_id = id;
        const response = await editNews(payload);
        if (response && response.data.statusCode === 200) {
          toast.success("News updated successfully!");
          navigate("/news-list");
        } else {
          toast.error(response?.data?.statusMessage || "Failed to update news");
        }
      } else {
        const response = await addNews(payload);
        if (response && response.data.statusCode === 200) {
          toast.success("News submitted successfully!");
          reset();
          setTags([]);
          navigate("/news-list");
        } else {
          toast.error(response?.data?.statusMessage || "Failed to add news");
        }
      }
    } catch (error) {
      console.error(
        `Error ${isEditMode ? "updating" : "submitting"} news:`,
        error
      );
      toast.error(
        `Failed to ${isEditMode ? "update" : "submit"} news. ${error.message}`
      );
    } finally {
      setIsLoading(false);
    }
  };

  const isSubmitDisabled = isLoading || isUploadingImage;

  return (
    <div
      className={`mb-3 ${admin_type === "sub_admin" ? "sub-admin-add" : ""}`}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="subheader-title">
          <div className="breadcrumps">
            <a onClick={() => navigate("/news-list")}>News</a>
            <FontAwesomeIcon icon={faAngleRight} />{" "}
            <a>{isEditMode ? "Edit" : "Add"} News</a>
          </div>
          <div className="subheader-right-btm">
            <button
              onClick={() => navigate("/news-list")}
              type="button"
              className="btn-purple"
              disabled={isLoading}
            >
              Discard
            </button>
            <button
              type="submit"
              className="btn-purple discard"
              disabled={isSubmitDisabled}
            >
              {isLoading
                ? `${isEditMode ? "Updating..." : "Saving..."}`
                : isUploadingImage
                ? "Uploading Image..."
                : `${isEditMode ? "Update" : "Save"}`}
            </button>
          </div>
        </div>

        <div>
          <div className="col-md-6 category">
            <h2>Select Category <span className="text-danger">*</span></h2>
            <select className="form-control" {...register("category")}>
              <option value="">Select Category</option>
              {newsCategories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name || cat.category_name || cat.title}
                </option>
              ))}
            </select>
            {errors.category && (
              <span className="text-danger">{errors.category.message}</span>
            )}
          </div>

          <div className="col-md-6">
            <h2>News Title <span className="text-danger">*</span></h2>
            <input
              className="form-control"
              placeholder="News Title"
              {...register("title")}
            />
            {errors.title && (
              <span className="text-danger">{errors.title.message}</span>
            )}
          </div>

          <div className="home row mt-3">
            <h6>Description <span className="text-danger">*</span></h6>
            <CustomEditorHosted
              initialValue={editorContent || ""}
              onChange={(e) => {
                const content = e.target.getContent();
                setValue(
                  "description",
                  content?.replaceAll(`"`, "%22")?.replaceAll(`'`, "%27")
                );
              }}
            />
            {errors.description && (
              <span className="text-danger">{errors.description.message}</span>
            )}
          </div>

          <div className="col-md-4">
            <h3>
              <FontAwesomeIcon className="play-icon" icon={faPlay} />
              Upload Cover Image <span className="text-danger">*</span>
            </h3>
            <div className="image-upload-container">
              <div
                onChange={(e) => {
                  if (e.target.files && e.target.files.length > 0) {
                    handleImageUploadStart();
                  }
                }}
              >
                <ImageUpload
                  inputId={"coverImage1"}
                  imgId={"coverImage"}
                  alt="Cover Image"
                  name="cover_image"
                  setValue={handleImageUpload}
                  onUpload={(imageUrl) => {
                    setValue("cover_image", imageUrl);
                    setIsUploadingImage(false);
                  }}
                  type="url"
                  src={watch("cover_image") || ""}
                />
              </div>

              {isUploadingImage && (
                <div className="upload-status">
                  <span>Uploading image... Please wait</span>
                </div>
              )}

              {watch("cover_image") && !isUploadingImage && (
                <div className="image-preview">
                  <img
                    src={watch("cover_image")}
                    alt="Cover Preview"
                    style={{ maxWidth: "100%", maxHeight: "200px" }}
                  />
                </div>
              )}

              {errors.cover_image && (
                <span className="text-danger">
                  {errors.cover_image.message}
                </span>
              )}
            </div>
          </div>

          <div className="col-md-12 news-additional-info">
            <div className="col-md-12 row mt-3 mb-3">
              <div className="col-md-6">
                <h2 style={{ color: "#ff8c00", fontWeight: "600" }}>
                  Main Tag <span className="text-danger">*</span>
                </h2>
                <Form.Select
                  className="form-control"
                  style={{
                    height: "45px",
                    backgroundColor: "#1b2438",
                    color: "#fff",
                    borderColor: "#283046",
                    borderRadius: "6px",
                  }}
                  value={mainTag}
                  onChange={(e) => {
                    setMainTag(e.target.value);
                    setSelectedSubTag("");
                  }}
                >
                  <option value="all">All Category</option>
                  <option value="courses">Courses</option>
                  <option value="careers">Careers</option>
                  <option value="country">Country</option>
                  <option value="colleges">Colleges</option>
                  <option value="entrance_exam">Entrance Exam</option>
                  <option value="specialization">Specialization</option>
                </Form.Select>
              </div>
              <div className="col-md-6">
                <h2 style={{ color: "#ff8c00", fontWeight: "600" }}>
                  Sub Tag <span className="text-danger">*</span>
                </h2>
                <Select
                  options={subTagOptions}
                  isLoading={isSubTagLoading}
                  value={
                    selectedSubTag
                      ? { value: selectedSubTag, label: selectedSubTag }
                      : null
                  }
                  onInputChange={(inputValue, { action }) => {
                    if (action === "input-change") {
                      fetchSubTagOptions(inputValue);
                    }
                  }}
                  onChange={(selectedOption) => {
                    if (selectedOption && selectedOption.value) {
                      const val = selectedOption.value;
                      if (!tags.includes(val)) {
                        setTags([...tags, val]);
                      }
                      setSelectedSubTag("");
                    } else {
                      setSelectedSubTag("");
                    }
                  }}
                  placeholder="🔍 Type to search Sub Tag..."
                  isSearchable={true}
                  isClearable={true}
                  styles={customSelectStyles}
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              </div>
              <div className="col-md-12 tags mt-3">
                <h2 style={{ color: "#ff8c00", fontWeight: "600" }}>
                  Added Tags
                </h2>
                <div
                  className="tags-input-container p-3 d-flex flex-wrap align-items-center gap-2"
                  style={{
                    minHeight: "80px",
                    backgroundColor: "#1b2438",
                    borderRadius: "8px",
                    border: "1px solid #283046",
                  }}
                >
                  {tags.map((tag, index) => (
                    <div
                      className="tag-item d-inline-flex align-items-center px-3 py-1 rounded me-2 mb-2"
                      key={index}
                      style={{
                        backgroundColor: "#283046",
                        color: "#ffffff",
                        fontSize: "13px",
                        border: "1px solid #3b4253",
                      }}
                    >
                      <span className="text me-2">{tag}</span>
                      <span
                        className="close"
                        style={{
                          cursor: "pointer",
                          color: "#ea5455",
                          fontWeight: "bold",
                        }}
                        onClick={() => handleRemoveTag(index)}
                      >
                        &times;
                      </span>
                    </div>
                  ))}
                  {tags.length === 0 && (
                    <span style={{ color: "#67748e", fontSize: "14px" }}>
                      No tags added yet. Select a Sub Tag to add tags.
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <h2>Time Taken (e.g. 4 min read) <span className="text-danger">*</span></h2>
              <input
                type="text"
                className="form-control"
                placeholder="Time Taken (e.g. 4 min read)"
                {...register("time_taken")}
              />
              {errors.time_taken && (
                <span className="text-danger">{errors.time_taken.message}</span>
              )}
            </div>

            <div className="row mt-3">
              <div className="col-md-6">
                <h2>Is Popular <span className="text-danger">*</span></h2>
                <div className="form-check form-check-inline">
                  <input
                    className="form-check-input-news newsradio"
                    type="radio"
                    id="popularYes"
                    value="1"
                    {...register("is_popular")}
                  />
                  <label className="form-check-label" htmlFor="popularYes">
                    Yes
                  </label>
                </div>
                <div className="form-check form-check-inline">
                  <input
                    className="form-check-input-news"
                    type="radio"
                    id="popularNo"
                    value="0"
                    {...register("is_popular")}
                  />
                  <label className="form-check-label" htmlFor="popularNo">
                    No
                  </label>
                </div>
                {errors.is_popular && (
                  <span className="text-danger">
                    {errors.is_popular.message}
                  </span>
                )}
              </div>

              <div className="col-md-6">
                <h2>Is Featured <span className="text-danger">*</span></h2>
                <div className="form-check form-check-inline">
                  <input
                    className="form-check-input-news newsradio"
                    type="radio"
                    id="featuredYes"
                    value="1"
                    {...register("is_featured")}
                  />
                  <label className="form-check-label" htmlFor="featuredYes">
                    Yes
                  </label>
                </div>
                <div className="form-check form-check-inline">
                  <input
                    className="form-check-input-news"
                    type="radio"
                    id="featuredNo"
                    value="0"
                    {...register("is_featured")}
                  />
                  <label className="form-check-label" htmlFor="featuredNo">
                    No
                  </label>
                </div>
                {errors.is_featured && (
                  <span className="text-danger">
                    {errors.is_featured.message}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AddNews;
